import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query'
import client from 'enrollment-platform/api/eps.client'
import { useEffect, useRef } from 'react'

export type ClearStatusValue = 'Open' | 'Clearing' | 'Failed'

export type ClearStatus = {
  status: ClearStatusValue
  total: number
  completed: number
  failed: number
  runId?: string
}

const CLEARING_POLL_INTERVAL_MS = 3000

/**
 * Polls the bulk cart-clear run status for a paused enrollment. Fetches once on
 * mount whenever the enrollment is paused (so a failed run shows on load) and
 * refetches every few seconds only while the run is still `Clearing`. When a run
 * transitions `Clearing -> Open` (the backend auto-lifts the pause on success),
 * the enrollment configuration query is invalidated so the card refreshes.
 */
export function useClearStatus(
  customerId: string,
  enrollmentId: string | undefined,
  enabled: boolean
): UseQueryResult<ClearStatus, Error> {
  const queryClient = useQueryClient()
  const previousStatus = useRef<ClearStatusValue | undefined>(undefined)
  const query = useQuery({
    queryKey: ['getClearStatus', customerId, enrollmentId],
    queryFn: () =>
      client
        .get<ClearStatus>(
          `/customers/${customerId}/configuration/enrollment/${enrollmentId}/clear-status`
        )
        .then((response) => response.data),
    enabled: enabled && !!enrollmentId,
    refetchInterval: (currentQuery) =>
      currentQuery.state.data?.status === 'Clearing'
        ? CLEARING_POLL_INTERVAL_MS
        : false,
    refetchIntervalInBackground: false,
    staleTime: 0,
  })
  const status = query.data?.status
  useEffect(() => {
    if (previousStatus.current === 'Clearing' && status === 'Open') {
      queryClient.invalidateQueries({
        queryKey: ['getEnrollmentConfiguration', customerId],
      })
    }
    previousStatus.current = status
  }, [status, customerId, queryClient])
  return query
}
